"use client";

import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface AlertContextType {
  showAlert: (message: string, title?: string, type?: 'success' | 'error' | 'info') => void;
  showConfirm: (message: string, onConfirm: () => void, title?: string, confirmText?: string, cancelText?: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
}

export function AlertProvider({ children }: { children: ReactNode }) {
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    message: string;
    title: string;
    type: 'success' | 'error' | 'info';
  }>({ isOpen: false, message: '', title: '', type: 'info' });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    message: string;
    title: string;
    confirmText: string;
    cancelText: string;
    onConfirm: (() => void) | null;
  }>({ isOpen: false, message: '', title: '', confirmText: 'Confirm', cancelText: 'Cancel', onConfirm: null });

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const showAlert = (message: string, title = 'Notification', type: 'success' | 'error' | 'info' = 'info') => {
    setAlertState({ isOpen: true, message, title, type });
  };

  const showConfirm = (message: string, onConfirm: () => void, title = 'Confirm', confirmText = 'Yes', cancelText = 'No') => {
    setConfirmState({ isOpen: true, message, title, confirmText, cancelText, onConfirm });
  };

  const closeAlert = () => setAlertState(prev => ({ ...prev, isOpen: false }));
  
  const closeConfirm = () => setConfirmState(prev => ({ ...prev, isOpen: false }));
  
  const handleConfirm = () => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm();
    }
    closeConfirm();
  };

  const renderModal = () => {
    if (!isClient || typeof document === 'undefined') return null;
    
    if (alertState.isOpen) {
      return createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: '0.75rem', padding: '2rem',
            maxWidth: '400px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.25rem', fontWeight: 600 }}>
              {alertState.title}
            </h3>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              {alertState.message}
            </p>
            <button
              onClick={closeAlert}
              style={{
                padding: '0.5rem 1.5rem', borderRadius: '0.5rem', border: 'none',
                background: 'var(--color-primary, #2563eb)', color: 'white',
                fontWeight: 500, cursor: 'pointer', width: '100%'
              }}
            >
              OK
            </button>
          </div>
        </div>,
        document.body
      );
    }

    if (confirmState.isOpen) {
      return createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: '0.75rem', padding: '2rem',
            maxWidth: '400px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.25rem', fontWeight: 600 }}>
              {confirmState.title}
            </h3>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              {confirmState.message}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={closeConfirm}
                style={{
                  padding: '0.5rem 1.5rem', borderRadius: '0.5rem', border: '1px solid #d1d5db',
                  background: 'white', color: '#374151', fontWeight: 500, cursor: 'pointer', flex: 1
                }}
              >
                {confirmState.cancelText}
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: '0.5rem 1.5rem', borderRadius: '0.5rem', border: 'none',
                  background: 'var(--color-primary, #2563eb)', color: 'white',
                  fontWeight: 500, cursor: 'pointer', flex: 1
                }}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
    }

    return null;
  };

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {renderModal()}
    </AlertContext.Provider>
  );
}
